import React, { useContext, useState } from 'react';
import { IFtseConfiguration, useUpdateFtseConfigurationMutation } from '../../generated/graphql';
import { AppContext } from '../../AppContext';
import { Actions } from '../../enums/ActionEnums';
import DialogBox from '../Common/Layout/DialogBox';
import { Box } from '@mui/material';
import InputComponent from '../Common/Fields/InputComponent';
import CancelButton from '../Common/Buttons/CancelButton';
import SaveButton from '../Common/Buttons/SaveButton';
import EditButton from '../Common/Buttons/EditButton';
import { Colors } from '../../utils/theme';

type IProps = {
  handleClose: () => void;
  ftseConfigurationInput: IFtseConfiguration;
};

export type IErrors = {
  value: string | null;
};

const defaultErrorsObj: IErrors = {
  value: null
};

const errorTexts = {
  value: 'Value is required'
};

const FtseConfigForm: React.FunctionComponent<IProps> = ({
  handleClose,
  ftseConfigurationInput
}: IProps) => {
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [editable, setEditable] = useState(false);
  const [property] = useState(ftseConfigurationInput.property);
  const [value, setValue] = useState(ftseConfigurationInput.value);
  const { dispatch } = useContext(AppContext);
  const [updateFtseConfiguration, updateFtseConfigurationResult] =
    useUpdateFtseConfigurationMutation();

  const handleCancel = () => {
    if (editable) {
      setValue(ftseConfigurationInput.value);
      setFormErrors(defaultErrorsObj);
      setEditable(false);
    } else {
      handleClose();
    }
  };

  const handleSubmit = () => {
    let hasErrors = false;
    const errors: IErrors = {
      value: null
    };
    if (!value || value.length < 1) {
      hasErrors = true;
      errors.value = errorTexts.value;
    }
    if (hasErrors) {
      setFormErrors(errors);
    } else {
      handleSave(ftseConfigurationInput as IFtseConfiguration);
    }
  };

  const handleSave = (ftseConfigurationInput: IFtseConfiguration) => {
    updateFtseConfiguration({
      variables: {
        id: ftseConfigurationInput.id as string,
        input: {
          value: value as string
        }
      },
      update() {
        handleClose();
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'success',
            text: 'Ftse configuration updated'
          }
        });
      }
    }).catch((e) => {
      dispatch({
        type: Actions.ShowMessage,
        payload: {
          severity: 'error',
          text: e.message
        }
      });
    });
  };

  const handleFtseConfigurationValueChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { value } = event.currentTarget;
    if (value && value.length > 0) {
      setFormErrors({ ...formErrors, value: null });
    } else {
      setFormErrors({ ...formErrors, value: errorTexts.value });
    }
    setValue(value);
  };

  return (
    <DialogBox id="ftseConfig-form-dialog-title" width={689} title={'Details'}>
      <Box sx={{ textAlign: 'right', width: '100%', marginTop: '10px', marginRight: '15px' }}>
        <EditButton
          id="ftseConfig-form-edit-button"
          onClick={() => setEditable(true)}
          disabled={editable}
          editButtonColor={editable ? Colors.gray : Colors.skyBlue}
        />
      </Box>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
          gap: '20px 18px',
          padding: '60px 30px',
          height: '370px'
        }}
      >
        <InputComponent
          id="ftseConfig-form-property"
          label="Property name"
          value={property || ''}
          disabled
          additionalStyle={{ gridColumn: 'span 4' }}
        />
        <InputComponent
          id="ftseConfig-form-value"
          label="Property value"
          value={value || ''}
          disabled={!editable || updateFtseConfigurationResult.loading}
          errorMsg={formErrors.value}
          additionalStyle={{ gridColumn: 'span 4' }}
          onChange={handleFtseConfigurationValueChange}
        />
        <Box className="centralize" sx={{ gridColumn: 'span 4', marginTop: '50px' }}>
          <CancelButton
            id="cancel-ftse-configuration-button"
            disabled={updateFtseConfigurationResult.loading}
            onClick={handleCancel}
            text={ftseConfigurationInput === null || editable ? 'Cancel' : 'Close'}
          />
          <SaveButton
            id="save-ftse-configuration-button"
            disabled={!editable || updateFtseConfigurationResult.loading}
            onClick={handleSubmit}
            text={'Save'}
          />
        </Box>
      </Box>
    </DialogBox>
  );
};

export default FtseConfigForm;
